// In-flight uploads, held outside React.
//
// The project tabs are child routes, so ProjectFilesTab unmounts the moment a
// user opens Bid Readiness or leaves the project. Anything about an upload that
// lived in that component's state died with it: the "uploading" placeholder
// rows vanished, the keys to poll were never recorded, and the POST's own
// completion handler wrote to a component that no longer existed. Coming back
// mid-upload showed an empty table until the server finished and a later fetch
// happened to include the files.
//
// So the upload lives here instead. A module outlives every route change and
// dies only on a full page reload, which aborts the request anyway.

import api from "../../../chat/api";

/** folderId -> { temps, optimistic, pollingKeys, uploading, error } */
const entries = new Map();
/** folderId -> Set<listener> */
const listeners = new Map();

const EMPTY = Object.freeze({
  temps: [],
  optimistic: [],
  pollingKeys: [],
  uploading: false,
});

const keyOf = (doc) => doc.key || doc.pdf_key || doc.id;

function entryFor(folderId) {
  const id = String(folderId);
  if (!entries.has(id)) {
    entries.set(id, { temps: [], optimistic: [], pollingKeys: [], uploading: false, error: null });
  }
  return entries.get(id);
}

function emit(folderId) {
  const id = String(folderId);
  const snapshot = getUploadState(id);
  (listeners.get(id) || []).forEach((fn) => {
    try {
      fn(snapshot);
    } catch {
      // A listener that throws must not stop the others, or an upload can
      // finish with half the UI never hearing about it.
    }
  });
  // Nothing in flight and nothing to hand over: stop holding the folder.
  const entry = entries.get(id);
  if (entry && !entry.uploading && entry.temps.length === 0
      && entry.optimistic.length === 0 && entry.pollingKeys.length === 0 && !entry.error) {
    entries.delete(id);
  }
}

/** A stable, plain snapshot. Never hand out the mutable entry itself. */
export function getUploadState(folderId) {
  const entry = entries.get(String(folderId));
  if (!entry) return EMPTY;
  return {
    temps: entry.temps,
    optimistic: entry.optimistic,
    pollingKeys: entry.pollingKeys,
    uploading: entry.uploading,
  };
}

export function subscribeUploads(folderId, listener) {
  const id = String(folderId);
  if (!listeners.has(id)) listeners.set(id, new Set());
  listeners.get(id).add(listener);
  return () => {
    const set = listeners.get(id);
    if (!set) return;
    set.delete(listener);
    if (set.size === 0) listeners.delete(id);
  };
}

/**
 * Read and clear the last upload failure.
 *
 * Cleared on read so that remounting the tab does not re-toast a failure the
 * user has already been told about.
 */
export function consumeUploadError(folderId) {
  const entry = entries.get(String(folderId));
  if (!entry?.error) return null;
  const { error } = entry;
  entry.error = null;
  emit(folderId);
  return error;
}

/**
 * Forget documents that have reached a terminal status.
 *
 * Called by the tab as it observes each document settle, so a folder does not
 * keep handing back rows the server is already reporting properly.
 */
export function releaseUploads(folderId, keys) {
  const entry = entries.get(String(folderId));
  if (!entry) return;
  const done = new Set(keys.map(String));
  if (done.size === 0) return;

  const before = entry.optimistic.length + entry.pollingKeys.length;
  entry.optimistic = entry.optimistic.filter((doc) => !done.has(String(keyOf(doc))));
  entry.pollingKeys = entry.pollingKeys.filter((k) => !done.has(String(k)));
  if (entry.optimistic.length + entry.pollingKeys.length !== before) emit(folderId);
}

/**
 * Upload files to a project, tracked independently of any mounted component.
 *
 * Resolves with { newDocs, failedDocs } for a caller that is still around to
 * care, and records the same outcome on the entry for one that is not.
 */
export async function startUpload(folderId, selectedFiles) {
  const id = String(folderId);
  const entry = entryFor(id);

  const now = new Date();
  const formattedDate = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;

  const temps = selectedFiles.map((file, idx) => ({
    id: `temp-${Date.now()}-${idx}`,
    name: file.name,
    status: "uploading",
    uploaded_at: formattedDate,
    file_type: file.name.split(".").pop()?.toLowerCase(),
    uploaded_by: "You",
    isTemp: true,
    tags: [],
  }));

  entry.temps = [...temps, ...entry.temps];
  entry.uploading = true;
  entry.error = null;
  emit(id);

  const formData = new FormData();
  selectedFiles.forEach((file) => formData.append("files", file));

  try {
    const response = await api.post(`/api/v1/rfp/folders/${id}/upload/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const uploadedResults =
      response.data.uploads || response.data.results || response.data.documents || [];
    const validNewKeys = uploadedResults.map((doc) => doc.pdf_key || doc.key || doc.id).filter(Boolean);
    const failedDocs = uploadedResults.filter((doc) => doc.status === "failed");

    const newDocs = uploadedResults
      .filter((doc) => (doc.pdf_key || doc.key || doc.id) && doc.status !== "failed")
      .map((doc) => ({
        id: doc.doc_id || doc.id || Date.now(),
        key: doc.pdf_key || doc.key || doc.id,
        name: doc.file || doc.name,
        status: doc.status || "processing",
        uploaded_at: doc.uploaded_at || formattedDate,
        file_type: (doc.file || doc.name)?.split(".").pop()?.toLowerCase() || "pdf",
        uploaded_by: doc.uploaded_by || "You",
        tags: [],
        isOptimistic: true,
      }));

    // A per-file rejection comes back inside a 202, not as a thrown error, so
    // it has to be recorded here too. Otherwise a duplicate rejected while the
    // user was on another tab takes its placeholder row away and says nothing.
    if (failedDocs.length > 0) {
      entry.error = `${failedDocs.length} file(s) failed: ${failedDocs[0].reason || 'Duplicate detected'}`;
    }

    // Only this batch's placeholders go; a second upload started while this one
    // was running keeps its own.
    const mine = new Set(temps.map((t) => t.id));
    entry.temps = entry.temps.filter((t) => !mine.has(t.id));
    entry.optimistic = [...newDocs, ...entry.optimistic];
    entry.pollingKeys = [...new Set([...entry.pollingKeys, ...validNewKeys])];
    entry.uploading = entry.temps.length > 0;
    emit(id);

    return { newDocs, failedDocs };
  } catch (error) {
    const mine = new Set(temps.map((t) => t.id));
    entry.temps = entry.temps.filter((t) => !mine.has(t.id));
    entry.uploading = entry.temps.length > 0;
    entry.error = "Failed to upload file(s)";
    emit(id);
    throw error;
  }
}
